/**
 * Russian for the 09 tour-search, Bronlar and catalogue screens.
 *
 * `uz-latin` is the only locale shipped (D12); this dictionary is kept complete for the keys
 * these screens own so a second locale is a lookup away. The type makes a missing key a
 * compile error, so the two dictionaries cannot drift.
 */
import type { TranslationKey } from "./uz";

export type Tours09Key = Extract<
  TranslationKey,
  `tq.${string}` | `bron.${string}` | `katalog.${string}` | `opconn.${string}`
>;

export const ru09Tours: Record<Tours09Key, string> = {
  "tq.title": "Поиск туров",
  "tq.tab.tours": "Туры",
  "tq.tab.hotels": "Только отель",
  "tq.tab.hot": "Горящие туры",
  "tq.tab.avia": "Авиабилеты",
  "tq.tab.packages": "Наши пакеты",
  "tq.tabsLabel": "Тип поиска",
  "tq.selectionButton": "Подборка · {count}",
  "tq.history": "История поиска",
  "tq.historyEmpty": "Поисков пока не было.",
  "tq.clientMode": "Для клиента: {name} · Сделка №{number}",
  "tq.clientModeNoDeal": "Для клиента: {name}",
  "tq.clientModeExit": "Выйти из режима клиента",
  "tq.addToSelection": "+ Подборка",
  "tq.addToSelectionLong": "+ В подборку",
  "tq.addedToSelection": "Добавлено в подборку",
  "tq.selectionNeedsOwner": "Подборка привязывается к сделке или клиенту. Сначала откройте режим клиента.",
  "tq.book": "Бронь ↗",
  "tq.bookTooltip": "Откроется на сайте оператора и привяжется к сделке",
  "tq.bookLinked": "Привязано к сделке №{number}",
  "tq.bookNoLink": "Привязано к сделке. Ссылки оператора нет.",
  "tq.filters": "Фильтры",
  "tq.filtersReset": "Сбросить",
  "tq.noFilterMatch": "Нет результатов по фильтру.",
  "tq.loadFailed": "Не удалось загрузить результаты.",
  "tq.find": "Найти",
  "tq.tours.aiLine": "Свободный текст: Египет, начало ноября, 2 взрослых + ребёнок 7 лет, до 1 500 $",
  "tq.tours.aiApply": "В поля",
  "tq.tours.aiUnresolved": "Нет в каталоге: {words}. Выберите вручную.",
  "tq.tours.departFlex": "Вылет (±{days} дн.)",
  "tq.tours.from": "Откуда",
  "tq.tours.to": "Куда",
  "tq.tours.toPlaceholder": "Выберите страну",
  "tq.tours.depart": "Вылет (±2 дня)",
  "tq.tours.flex": "±2 дня",
  "tq.tours.duration": "Продолжительность",
  "tq.tours.nights": "{from}–{to} ночей",
  "tq.tours.who": "Кто едет",
  "tq.tours.adults": "Взрослые",
  "tq.tours.childAges": "Возраст детей (через запятую)",
  "tq.tours.quick": "Быстрый выбор",
  "tq.tours.quickFrom": "{country} · от {price}",
  "tq.country.title": "Выбор страны",
  "tq.country.charterOnly": "Только чартер",
  "tq.country.directOnly": "Прямой рейс",
  "tq.country.single": "Одна страна",
  "tq.country.multi": "Несколько стран",
  "tq.country.search": "Поиск страны",
  "tq.country.popular": "Популярные — ваши самые продаваемые",
  "tq.country.all": "Все страны",
  "tq.country.done": "Выбрать",
  "tq.country.selected": "{count} стран",
  "tq.calendar.title": "Календарь цен",
  "tq.calendar.cheapest": "самый дешёвый день",
  "tq.filter.resort": "Курорт",
  "tq.filter.anyResort": "Любой курорт",
  "tq.filter.level": "Категория отеля",
  "tq.filter.stars": "{count} звезды и выше",
  "tq.filter.board": "Питание",
  "tq.filter.operators": "Туроператоры",
  "tq.filter.important": "Важно",
  "tq.filter.seatsOnly": "Только с местами",
  "tq.filter.halalOnly": "Халяль (с источником)",
  "tq.results.summary": "{hotels} отелей · от {operators} операторов",
  "tq.sort.label": "Сортировка",
  "tq.sort.cheapest": "Самые дешёвые",
  "tq.sort.rating": "Рейтинг",
  "tq.sort.bestseller": "Мы чаще продаём",
  "tq.sort.nearCenter": "Ближе к центру",
  "tq.showNetto": "Показать нетто",
  "tq.card.cheapest": "самый дешёвый",
  "tq.card.stars": "{count} звезды",
  "tq.card.placePhoto": "Фото: {place}, не отель · {credit}",
  "tq.card.moreOperators": "Ещё цены {count} операторов →",
  "tq.card.fewer": "Свернуть",
  "tq.card.soldTimes": "За последний месяц вы продали этот отель {count} раз",
  "tq.card.details": "Подробнее",
  "tq.card.netto": "нетто {netto} · прибыль {profit}",
  "tq.card.nights": "{count} ночей",
  "tq.avail.flightYes": "Есть места на рейс",
  "tq.avail.flightFew": "Мало мест на рейс",
  "tq.avail.flightNo": "Нет мест на рейс",
  "tq.avail.hotelGuaranteed": "Гарантия в отеле",
  "tq.avail.hotelRequest": "Отель под запрос",
  "tq.hotels.city": "Город или отель",
  "tq.hotels.checkIn": "Заезд",
  "tq.hotels.checkOut": "Выезд",
  "tq.hotels.nightsAuto": "{count} ночей",
  "tq.hotels.guests": "Гости",
  "tq.hotels.hint": "Введите город и даты, затем нажмите «Найти».",
  "tq.hotels.unsupported": "Поставщик отелей не подключён, поэтому этот поиск пока не работает.",
  "tq.hotels.empty": "На эти даты отели не найдены.",
  "tq.hotels.perNight": "1 ночь",
  "tq.hotels.total": "итого {total}",
  "tq.hotels.freeCancel": "Бесплатная отмена — до {date}",
  "tq.hotels.nonRefundable": "Невозвратный тариф",
  "tq.hotels.suppliers": "Поставщики",
  "tq.hotels.area": "Район",
  "tq.hotels.conditions": "Условия",
  "tq.hotels.freeCancelFilter": "Бесплатная отмена",
  "tq.hotels.breakfast": "Завтрак включён",
  "tq.hotels.instant": "Мгновенное подтверждение",
  "tq.hotels.nearBeach": "Близко к пляжу",
  "tq.hotels.pricePerNight": "Цена за ночь",
  "tq.hotels.priceMax": "до",
  "tq.hotels.map": "Карта",
  "tq.hotels.mapLarger": "Карта крупнее",
  "tq.hotels.mapSmaller": "Карта меньше",
  "tq.hotels.mapNoPoints": "У результатов нет координат.",
  "tq.hotels.beach": "до пляжа {meters} м",
  "tq.hotels.center": "до центра {km} км",
  "tq.hot.from": "Откуда",
  "tq.hot.within": "До вылета",
  "tq.hot.days": "{count} дней",
  "tq.hot.budget": "На двоих, до",
  "tq.hot.updated": "● обновлено {minutes} мин назад",
  "tq.hot.updatedNow": "● только что обновлено",
  "tq.hot.allCountries": "Все",
  "tq.hot.banner": "Этим предложениям подходят {count} ваших клиентов",
  "tq.hot.bannerOpen": "Посмотреть подходящих клиентов",
  "tq.hot.empty": "Горящих предложений пока нет.",
  "tq.hot.seatsLeft": "Осталось мест: {count}",
  "tq.hot.matched": "Подходит клиентов: {count}",
  "tq.hot.observed": "старая цена замечена {date}",
  "tq.hot.sendToClient": "Отправить клиенту",
  "tq.hot.today": "Сегодня, {time}",
  "tq.hot.tomorrow": "Завтра, {time}",
  "tq.hot.inDays": "Через {days} дн., {time}",
  "tq.hot.departed": "Уже вылетел",
  "tq.hot.matchesTitle": "Подходящие клиенты",
  "tq.hot.consentNote": "Отправляется только клиентам, давшим согласие на рассылку.",
  "tq.hot.noConsent": "Нет согласия — не отправляется",
  "tq.hot.blocked": "Сообщение не подготовлено: {reason}",
  "tq.hot.sendSelected": "Отправить выбранным",
  "tq.hot.sent": "{queued} сообщений в очереди, {skipped} пропущено без согласия",
  "tq.hot.noMatches": "Для этого предложения подходящих клиентов нет.",
  "tq.hot.pickOffer": "Предложение",
  "tq.avia.roundTrip": "Туда и обратно",
  "tq.avia.oneWay": "В одну сторону",
  "tq.avia.multi": "Сложный маршрут",
  "tq.avia.multiNote": "Сложный маршрут составьте на сайте партнёра: источник цен ищет только простые направления.",
  "tq.avia.swap": "Поменять местами откуда и куда",
  "tq.avia.direct": "Только прямые",
  "tq.avia.baggage": "С багажом",
  "tq.avia.flex": "Даты ±3 дня",
  "tq.avia.best.cheapest": "Самый дешёвый",
  "tq.avia.best.fastest": "Самый быстрый",
  "tq.avia.best.optimal": "Оптимальный",
  "tq.avia.stops": "Пересадки",
  "tq.avia.stopsDirect": "Прямой",
  "tq.avia.stopsOne": "1 пересадка",
  "tq.avia.stopsMany": "2+ пересадки",
  "tq.avia.airlines": "Авиакомпании",
  "tq.avia.departTime": "Время вылета",
  "tq.avia.airports": "Аэропорт",
  "tq.avia.night": "Ночь (00–06)",
  "tq.avia.morning": "Утро (06–12)",
  "tq.avia.day": "День (12–18)",
  "tq.avia.evening": "Вечер (18–24)",
  "tq.avia.buy": "Купить ↗",
  "tq.pkg.new": "+ Новый пакет",
  "tq.pkg.kpi.active": "Активные пакеты",
  "tq.pkg.kpi.sold": "Продано мест",
  "tq.pkg.kpi.revenue": "Выручка (брутто)",
  "tq.pkg.kpi.margin": "Средняя наценка",
  "tq.pkg.kpi.top": "Самый продаваемый",
  "tq.pkg.tab.active": "Активные",
  "tq.pkg.tab.draft": "Черновики",
  "tq.pkg.tab.archived": "Архив",
  "tq.pkg.search": "Поиск пакета",
  "tq.pkg.empty": "В этом разделе пакетов нет.",
  "tq.pkg.sold": "Продано {sold} / {quota} мест · {pct}%",
  "tq.pkg.perPerson": "за 1 человека",
  "tq.pkg.edit": "Редактировать",
  "tq.pkg.seatsLeft": "{count} мест",
  "tq.pkg.tag.hit": "Хит",
  "tq.pkg.tag.family": "Семейный",
  "tq.pkg.tag.new": "Новый",
  "tq.pkg.tag.umrah": "Умра",
  "tq.pkg.tag.none": "Без тега",
  "tq.pkg.form.newTitle": "Новый пакет",
  "tq.pkg.form.editTitle": "Редактирование пакета",
  "tq.pkg.form.name": "Название",
  "tq.pkg.form.country": "Страна",
  "tq.pkg.form.tag": "Тег",
  "tq.pkg.form.included": "Что включено (через запятую)",
  "tq.pkg.form.description": "Краткое описание",
  "tq.pkg.form.nights": "Количество ночей",
  "tq.pkg.form.netto": "Нетто",
  "tq.pkg.form.markup": "Наценка, %",
  "tq.pkg.form.brutto": "Брутто: {price}",
  "tq.pkg.form.price": "Цена (брутто)",
  "tq.pkg.form.currency": "Валюта",
  "tq.pkg.form.images": "Ссылки на фото (каждая с новой строки, https://)",
  "tq.pkg.form.departures": "Даты выезда",
  "tq.pkg.form.departureDate": "Дата",
  "tq.pkg.form.seats": "Количество мест",
  "tq.pkg.form.addDeparture": "+ Добавить дату",
  "tq.pkg.form.status": "Статус",
  "tq.pkg.form.saved": "Пакет сохранён",
  "tq.pkg.form.failed": "Не удалось сохранить пакет",
  "tq.pkg.send": "Отправить клиенту",
  "tq.pkg.noDepartures": "Нет дат вылета",
  "tq.pkg.netto": "нетто {price}",
  "tq.pkg.form.removeDeparture": "Убрать дату",
  "tq.sel.title": "Подборка",
  "tq.sel.empty": "Подборка пуста. Нажмите «+ Подборка» в результатах.",
  "tq.sel.remove": "Убрать",
  "tq.sel.send": "Отправить клиенту",
  "tq.sel.telegram": "Сообщение в Telegram",
  "tq.sel.pdf": "PDF",
  "tq.sel.bruttoOnly": "Клиенту уходит только цена брутто.",
  "tq.sel.sent": "Сообщение в Telegram поставлено в очередь",
  "tq.sel.failed": "Не удалось отправить",
  "tq.sel.kind.tour": "Тур",
  "tq.sel.kind.hotel": "Отель",
  "tq.sel.kind.hot": "Горящий тур",
  "tq.sel.kind.avia": "Авиабилет",
  "tq.sel.kind.package": "Пакет",
  "bron.title": "Брони",
  "bron.subtitle": "Брони у всех туроператоров в одном месте · подключено операторов: {count}",
  "bron.fromSearch": "+ Бронь из поиска туров",
  "bron.alert.title": "{count} важных сообщений — туристам ещё не сообщили",
  "bron.alert.informed": "Туристам сообщили",
  "bron.alert.informedBy": "{name} · {time}",
  "bron.alert.clientMessage": "Сообщение клиенту",
  "bron.ops.all": "Все",
  "bron.ops.updated": "обновлено {minutes} мин назад",
  "bron.ops.updatedNever": "Ещё не синхронизировано",
  "bron.ops.broken": "Соединение прервано — войдите снова",
  "bron.ops.counts": "{bookings} броней · {messages} сообщений",
  "bron.ops.debt": "Долг: {amount}",
  "bron.ops.refund": "Возврат: {amount}",
  "bron.ops.cabinet": "Войти в кабинет ↗",
  "bron.ops.reconnect": "Переподключить ↗",
  "bron.ops.add": "+ Подключить оператора",
  "bron.reconnect.title": "{operator} — переподключение",
  "bron.reconnect.login": "Логин",
  "bron.reconnect.password": "Пароль",
  "bron.reconnect.submit": "Подключить",
  "bron.reconnect.ok": "Соединение восстановлено",
  "bron.reconnect.failed": "Не удалось подключиться",
  "bron.filter.search": "№ брони, турист или клиент",
  "bron.filter.operator": "Оператор",
  "bron.filter.status": "Статус",
  "bron.filter.anyStatus": "Любой статус",
  "bron.filter.next30": "Вылет: ближайшие 30 дней",
  "bron.filter.newOnly": "Только с новыми сообщениями",
  "bron.col.booking": "№ брони · оператор",
  "bron.col.tour": "Тур · клиент",
  "bron.col.dates": "Даты · туристы",
  "bron.col.status": "Статус",
  "bron.col.payment": "Оплата оператору",
  "bron.col.messages": "Сообщения",
  "bron.messagesButton": "Сообщения · {count}",
  "bron.linkDeal": "Привязать к сделке",
  "bron.linkDealTitle": "Привязать бронь к сделке",
  "bron.linkDealPick": "Сделка",
  "bron.linked": "Привязано к сделке",
  "bron.deal": "Сделка №{number}",
  "bron.travellers": "{count} туристов",
  "bron.empty": "Броней по этому фильтру нет.",
  "bron.panel.openSite": "Открыть на сайте оператора ↗",
  "bron.panel.close": "Закрыть панель",
  "bron.topic.all": "Все",
  "bron.topic.booking": "Бронирование",
  "bron.topic.avia": "Авиа",
  "bron.topic.payment": "Оплата",
  "bron.topic.documents": "Документы",
  "bron.panel.aiNote": "Комментарий ИИ",
  "bron.panel.summary": "Кратко от ИИ",
  "bron.panel.noSummary": "Краткого изложения для этих сообщений пока нет.",
  "bron.panel.prepareClient": "Подготовить сообщение клиенту",
  "bron.panel.draftBlocked": "Сообщение не подготовлено: {reason}",
  "bron.panel.noMessages": "Сообщений по этой брони нет.",
  "bron.panel.replyTopic": "Тема",
  "bron.panel.replyUz": "Напишите ответ по-узбекски",
  "bron.panel.translate": "Перевести на русский",
  "bron.panel.translated": "Текст для оператора (на русском)",
  "bron.panel.send": "Отправить оператору",
  "bron.panel.sent": "Отправлено оператору",
  "bron.panel.replyOnSite": "Этому оператору отвечают только на сайте",
  "bron.panel.passport": "Паспорт",
  "bron.panel.important": "Важно",
  "bron.ops.label": "Подключённые операторы",
  "bron.alert.open": "Открыть бронь",
  "katalog.title": "Сопоставление каталога",
  "katalog.badge": "Только владелец и админ",
  "katalog.banner": "Каждый оператор называет направления, отели, питание и номера по-своему. Kapitan сопоставляет их с нашим каталогом. Если уверенность ниже 90%, сопоставление подтверждаете вы. Не найденное в каталоге в поиске не показывается.",
  "katalog.tab.destination": "Направления",
  "katalog.tab.hotel": "Отели",
  "katalog.tab.board": "Типы питания",
  "katalog.tab.room": "Типы номеров",
  "katalog.kpi.matched": "Сопоставлено",
  "katalog.kpi.review": "Нужна проверка",
  "katalog.kpi.unmatched": "Не найдено в каталоге",
  "katalog.kpi.unmatchedHint": "эти туры не показываются в поиске",
  "katalog.kpi.lastSync": "Последняя синхронизация",
  "katalog.kpi.never": "Ещё не было",
  "katalog.filter.operator": "Оператор",
  "katalog.filter.allOperators": "Все операторы",
  "katalog.filter.reviewFirst": "Сначала требующие проверки",
  "katalog.filter.search": "Поиск по названию",
  "katalog.col.operator": "Оператор",
  "katalog.col.external": "Название у оператора",
  "katalog.col.catalog": "В нашем каталоге",
  "katalog.col.confidence": "Уверенность",
  "katalog.col.action": "Действие",
  "katalog.notFound": "Не найдено",
  "katalog.accept": "Подтвердить",
  "katalog.change": "Изменить",
  "katalog.addEntry": "Добавить в каталог",
  "katalog.changeTitle": "Изменить сопоставление",
  "katalog.changeId": "ID записи каталога",
  "katalog.reject": "Сопоставление неверно",
  "katalog.saved": "Сохранено",
  "katalog.empty": "В этом разделе сопоставлений нет.",
  "opconn.title": "Туроператоры",
  "opconn.subtitle": "Туроператоры, поставщики отелей и авиапартнёр: доступы, статус и возможности.",
  "opconn.group.tour": "Туроператоры",
  "opconn.group.hotel": "Поставщики отелей",
  "opconn.group.avia": "Авиапартнёр",
  "opconn.capabilities": "Возможности",
  "opconn.lastSync": "Последняя синхронизация: {time}",
  "opconn.lastSyncNever": "Ещё не синхронизировано",
  "opconn.health.ok": "Работает",
  "opconn.health.failing": "Ошибка",
  "opconn.health.unknown": "Не проверено",
  "opconn.test": "Проверить подключение",
  "opconn.testOk": "Подключение работает",
  "opconn.testFailed": "Подключение не работает: {detail}",
  "opconn.add": "+ Добавить доступ",
  "opconn.rotate": "Сменить пароль",
  "opconn.deactivate": "Отключить",
  "opconn.inactive": "Отключён",
  "opconn.noCredential": "Доступа нет",
  "opconn.form.provider": "Поставщик",
  "opconn.form.login": "Логин",
  "opconn.form.password": "Пароль",
  "opconn.form.newPassword": "Новый пароль",
  "opconn.saved": "Сохранено",
  "opconn.failed": "Не удалось сохранить",
  "opconn.deactivateConfirm": "Этот доступ будет отключён, и поиск через него прекратится.",
  "opconn.empty": "Список поставщиков пуст.",
};
